import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

const API_BASE =
  process.env.REACT_APP_SELLER_API ||
  "https://gallery.pitics.com";

const ROOTS = [
  { id: "personalized", name: "Personalized" },
  { id: "semi_personalized", name: "Semi-Personalized" },
  { id: "non_personalized", name: "Non Personalized" }
];

export default function SellerCategorySuggestions() {
  const [rootCategories, setRootCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [type, setType] = useState("category");
  const [rootId, setRootId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryName, setSubcategoryName] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = async () => auth.currentUser?.getIdToken();

  const load = async () => {
    const t = await token();
    if (!t) return;
    const headers = { "X-Firebase-Authorization": t };
    const [allowedRes, mineRes] = await Promise.all([
      fetch(`${API_BASE}/api/seller/allowed-categories`, { headers }),
      fetch(`${API_BASE}/api/seller/category-suggestions/mine`, { headers })
    ]);
    const [allowed, mine] = await Promise.all([allowedRes.json(), mineRes.json()]);
    if (!allowed.success) throw new Error(allowed.error || "Failed to load categories");
    if (!mine.success) throw new Error(mine.error || "Failed to load suggestions");
    setRootCategories(allowed.rootCategories || []);
    setCategories(allowed.categories || []);
    setSuggestions(mine.suggestions || []);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setLoading(false); return; }
      try { await load(); } catch (e) { alert(e.message); }
      finally { setLoading(false); }
    });
    return () => unsub();
  }, []);

  const selectedRoot = rootCategories.find((r) => r.id === rootId);
  const rootCategoryOptions = rootCategories.length ? rootCategories : ROOTS;
  const selectedCategory = categories.find(
    (c) => String(c.category).toLowerCase() === String(categoryId).toLowerCase()
  );
  const isAllProductsValue = (value) =>
    String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "") === "allproducts";

  const normalizedCategoryId = String(categoryId || "").trim().toLowerCase();
  const isAllProducts =
    isAllProductsValue(normalizedCategoryId) ||
    isAllProductsValue(selectedCategory?.category) ||
    isAllProductsValue(selectedCategory?.name);

  const categoryOptions = useMemo(() => {
    if (!rootId) return categories;
    return selectedRoot?.categories || [];
  }, [rootId, selectedRoot, categories]);

  const subcategoryOptions = selectedCategory?.subcategories || [];
  const childOptions = selectedCategory?.subsubcategoryMap?.[String(subcategoryName).toLowerCase()] || [];

  const resetForType = (next) => {
    setType(next);
    setName("");
    setNote("");
    setCategoryId("");
    setSubcategoryName("");
    if (next !== "category") setRootId("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert("Enter the suggested name.");
    if (type === "category" && !rootId) return alert("Select a root category.");
    if ((type === "subcategory" || type === "subsubcategory") && !categoryId) return alert("Select a category.");
    if (type === "subsubcategory" && !isAllProducts) return alert("Subcategory 2 can only be created under All Products. Select All Products as the category.");
    if (type === "subsubcategory" && !subcategoryName) return alert("Select the parent subcategory before continuing.");
    if (type === "subsubcategory" && subcategoryOptions.length === 0) return alert("No approved parent subcategories are available under All Products yet.");

    try {
      setSaving(true);
      const t = await token();
      const body = {
        type,
        name: name.trim(),
        rootCategoryId: rootId,
        categoryId,
        categoryName: type === "category" ? "" : String(selectedCategory?.category || selectedCategory?.name || categoryId),
        subcategoryName: type === "subsubcategory" ? subcategoryName : "",
        note: note.trim()
      };
      const res = await fetch(`${API_BASE}/api/seller/category-suggestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Firebase-Authorization": t },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to submit suggestion");
      alert("Suggestion submitted for Seller Admin approval.");
      setName(""); setNote(""); setSubcategoryName("");
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={styles.card}>Loading category suggestions...</div>;

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <h2 style={styles.title}>Suggest Category / Subcategory</h2>
        <p style={styles.muted}>
          Suggestions are reviewed by Seller Admin before they become marketplace categories.
        </p>

        <form onSubmit={submit} style={styles.grid}>
          <select value={type} onChange={(e) => resetForType(e.target.value)} style={styles.input}>
            <option value="category">New Category</option>
            <option value="subcategory">New Subcategory</option>
            <option value="subsubcategory">New Subcategory 2</option>
          </select>

          {type === "category" && (
            <select value={rootId} onChange={(e) => setRootId(e.target.value)} style={styles.input} required>
              <option value="">Select Root Category</option>
              {rootCategoryOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}

          {type !== "category" && (
            <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setSubcategoryName(""); }} style={styles.input} required>
              <option value="">Select Category</option>
              {categoryOptions.map((c) => <option key={c.category} value={c.category}>{c.category}</option>)}
            </select>
          )}

          {type === "subsubcategory" && (
            <select
              value={subcategoryName}
              onChange={(e) => setSubcategoryName(e.target.value)}
              style={{
                ...styles.input,
                opacity: isAllProducts ? 1 : 0.65,
                cursor: isAllProducts ? "pointer" : "not-allowed"
              }}
              required={isAllProducts}
              disabled={!isAllProducts}
              aria-label="Parent Subcategory for Subcategory 2"
            >
              <option value="">
                {isAllProducts
                  ? (subcategoryOptions.length ? "Select Parent Subcategory" : "No approved parent subcategories")
                  : "Select All Products category first"}
              </option>
              {isAllProducts && subcategoryOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}

          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={type === "subsubcategory" ? "Suggested Subcategory 2 name" : `Suggested ${type === "category" ? "category" : "subcategory"} name`}
            style={styles.input}
            maxLength={150}
            required
          />

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note for Seller Admin"
            rows={4}
            maxLength={1000}
            style={styles.input}
          />

          <button type="submit" disabled={saving} style={styles.button}>
            {saving ? "Submitting..." : "Submit for Admin Approval"}
          </button>
        </form>
      </div>

      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>My Suggestions</h3>
        {suggestions.length === 0 ? (
          <div style={styles.muted}>No suggestions submitted yet.</div>
        ) : suggestions.map((s) => (
          <div key={s.id} style={styles.row}>
            <div>
              <strong>{s.name}</strong>
              <div style={styles.muted}>
                {s.type === "category" ? "Category" : s.type === "subcategory" ? "Subcategory" : "Subcategory 2"}
                {s.categoryName ? ` • ${s.categoryName}` : ""}
                {s.subcategoryName ? ` • Parent: ${s.subcategoryName}` : ""}
              </div>
            </div>
            <span style={{ ...styles.badge, ...(s.status === "approved" ? styles.good : s.status === "rejected" ? styles.bad : {}) }}>
              {s.status || "pending"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  wrap: { display: "grid", gap: 18 },
  card: { background: "#fff", borderRadius: 24, padding: 24, boxShadow: "0 14px 40px rgba(0,0,0,.08)" },
  title: { margin: 0, fontSize: 24, fontWeight: 900 },
  muted: { color: "#64748b", fontSize: 13, lineHeight: 1.5 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14, marginTop: 18 },
  input: { width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 14, border: "1px solid #d1d5db", background: "#fff" },
  button: { border: 0, borderRadius: 14, padding: "13px 16px", fontWeight: 900, color: "#fff", background: "linear-gradient(135deg,#2563eb,#7c3aed)", cursor: "pointer" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "14px 0", borderBottom: "1px solid #eef2f7" },
  badge: { padding: "6px 10px", borderRadius: 999, background: "#fef3c7", fontWeight: 800, fontSize: 12 },
  good: { background: "#dcfce7" },
  bad: { background: "#fee2e2" }
};
